import { ComponentProps } from 'react'
import styled from 'styled-components'

import {
  TypeStyle,
  TypeStyleBodySmall,
  fromMd,
  timingFast,
  untilMd,
  withHoverSupport,
} from '@syconium/little-miss-figgy'

import { FavoritesButton } from '../../../../../app/_components/buttons/FavoritesButton.client'
import { NextLink } from '../../../../../app/_components/navigation/NextLink'
import { useFavorites } from '../../../../../app/_providers/FavoritesProvider.client'

import { PromoText } from './styles'

export const ProductDetailsTitle = styled(TypeStyle.BodyNano)<{
  $hasBackgroundColor?: boolean
  $hasIcon?: boolean
}>`
  font-weight: 600;
  ${o =>
    o.$hasIcon &&
    `
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${o.theme.spacing(2)};
  `}
  ${o =>
    o.$hasBackgroundColor &&
    `
    background-color: ${o.theme.color.fill.subtle};
    padding: ${o.theme.spacing(2)};
    border-radius: ${o.theme.spacing(1)};
  `}
`

export const IntlMsgWrap = styled(ProductDetailsTitle)`
  text-align: center;
  margin: ${o => o.theme.spacing(6)};

  ${fromMd} {
    margin: ${o => o.theme.spacing(4)} 0;
  }
`

export const PromoExclusionMessage = styled(TypeStyle.BodySmall)`
  color: ${o => o.theme.color.deprecated.obsidianLighter};
  text-align: center;
  font-weight: 400;
  font-size: 12px;
  margin: ${o => o.theme.spacing(6)};

  ${fromMd} {
    margin: ${o => o.theme.spacing(6)} 0;
  }
`

export const WhyWeLoveDivider = styled.hr`
  border-color: ${o => o.theme.color.deprecated.stoneLight};
  border-style: solid;
  border-width: 1px 0 0;
  margin: 0 ${o => o.theme.dimensions.layout.sidePadding.md}px;
`

export const WhyWeLoveText = styled(TypeStyle.HeadlineExtraExtraSmall)`
  border-top: 1px solid ${o => o.theme.color.deprecated.stoneLight};
  font-weight: 400;
  padding: 24px 0;
`

export const PDPFavoritesButtonContainer = styled(
  (props: { children?: React.ReactNode; className?: string }) => {
    const { status } = useFavorites()
    if (status === 'rejected') return null
    return <div className={props.className}>{props.children}</div>
  }
)`
  border: 1px solid #cccccc;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;

  > * {
    flex-basis: auto;
    flex-grow: 0;
    flex-shrink: 0;
  }
`

export const PDPFavoritesButton = ({
  iconWidth = '18px',
  ...rest
}: ComponentProps<typeof FavoritesButton>) => {
  return <FavoritesButton iconWidth={iconWidth} {...rest} />
}

export const ErrorMessageContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};

  ${untilMd} {
    margin-top: ${({ theme }) => theme.spacing(4)};
    padding: ${({ theme }) => theme.spacing(0, 4, 0)};
  }
`

export const LowInventoryTypography = styled(TypeStyleBodySmall)`
  color: ${o => o.theme.color.text.caution.on.background};
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(2)};
  margin: ${o => o.theme.spacing(4, 4)};
  ${fromMd} {
    margin: ${o => o.theme.spacing(4, 0)};
  }
`

export const PortalPromoText = styled(PromoText)`
  /* Apply padding for promo text used in tab content */
  padding: ${o => o.theme.spacing(0, 0, 0, 4)};
  ${untilMd} {
    padding-left: 16px;
    padding-right: 16px;
  }
`

export const TabContainer = styled.div`
  display: flex;
  margin-top: ${o => o.theme.spacing(3)};
  padding: ${o => o.theme.spacing(0, 4)};
  ${fromMd} {
    padding: 0;
  }
`

export const TabText = styled(TypeStyle.HeadlineMicro).attrs({
  isUppercase: true,
  textTransform: 'uppercase',
  asTag: 'span',
})`
  font-weight: 700;
`

export const Tab = styled(NextLink)`
  flex-basis: 100%;
  text-align: center;
  padding: ${o => o.theme.spacing(3)} 0;
  border-style: solid;
  transition: ${timingFast} background-color ease-out;
  border-radius: 4px 4px 0px 0px;

  /* active styles are displayed when 'aria-current' is true */
  border-bottom-width: ${o => (o['aria-current'] ? '2px' : '1px')};
  border-color: ${o =>
    o['aria-current'] ? o.theme.color.deprecated.black.primary : o.theme.color.deprecated.stone};
  pointer-events: ${o => (o['aria-current'] ? 'none' : 'initial')};

  /* Change font weight of tab text when active */
  ${TabText} {
    font-weight: ${o => (o['aria-current'] ? 700 : 600)};
  }

  /* Apply hover styling to tabs on pointer devices */
  ${withHoverSupport} {
    &:hover {
      background-color: ${o => !o['aria-current'] && o.theme.color.deprecated.stoneLighter};
    }
  }
`
